<script setup>
import { ref, computed, watch } from 'vue'
import Alert from '@/components/Atoms/Alert.vue'

// Colours for the audience panel in the embed player, chosen from the
// dashboard.
//
// A host picks a preset or four colours; the player renders them. The four
// colours only take effect under the `custom` preset, but they are stored
// whatever the preset is, so switching to `light` to look at it and back to
// `custom` does not cost someone the palette they built.

const props = defineProps({
	/** The `theme` block, possibly absent on a stream saved before it existed. */
	theme: {
		type: Object,
		default: null
	},
	/**
	 * The stream's existing player theme colour. It already drives the panel's
	 * accent, so the accent is never really unset and the UI must not pretend
	 * otherwise.
	 */
	embedThemeColor: {
		type: String,
		default: ''
	},
	disabled: {
		type: Boolean,
		default: false
	}
})

const emit = defineEmits(['update'])

// MIRRORS THE PLAYER — the single copy of these values in the dashboard.
//
// The palettes and the colour maths below are lifted from `resolveTheme` in the
// embed player's assets/scripts/audience.js, which is what actually renders the
// panel. They are duplicated here only so the preview can show a host what they
// are choosing before it reaches a viewer. `dark` is the panel's existing look,
// so a stream saved before theming existed renders exactly as it does today.
// If the player's palettes or its contrast rules move, move these with them.
const PRESETS = {
	dark: { label: 'Dark', background: '#14161a', surface: '#1a1d23', text: '#e8eaed', accent: '#417dfd' },
	light: { label: 'Light', background: '#ffffff', surface: '#f1f3f6', text: '#1b1f24', accent: '#1a56db' },
	blue: { label: 'Blue', background: '#0e1a2b', surface: '#16273f', text: '#e6eefb', accent: '#4da3ff' }
}

const PRESET_ORDER = ['dark', 'light', 'blue']

const HEX_PATTERN = /^#[0-9a-fA-F]{6}$/

const BLACK = { r: 0, g: 0, b: 0 }
const WHITE = { r: 255, g: 255, b: 255 }

const FIELDS = [
	{ key: 'background', label: 'Background', hint: 'The panel behind everything.' },
	{ key: 'surface', label: 'Cards', hint: 'Message rows, the pinned bar, the composer box.' },
	{ key: 'text', label: 'Text', hint: 'Message text and names.' },
	{ key: 'accent', label: 'Accent', hint: 'Buttons, the active tab, links.' }
]

/* --------------------------- colour maths ---------------------------- */

function parseColor(value) {
	if (typeof value !== 'string' || !HEX_PATTERN.test(value.trim())) return null

	const hex = value.trim().slice(1)
	return {
		r: parseInt(hex.slice(0, 2), 16),
		g: parseInt(hex.slice(2, 4), 16),
		b: parseInt(hex.slice(4, 6), 16)
	}
}

function toHex(color) {
	return `#${[color.r, color.g, color.b]
		.map((n) => Math.max(0, Math.min(255, Math.round(n))).toString(16).padStart(2, '0'))
		.join('')}`
}

function toTriplet(color) {
	return [color.r, color.g, color.b].map((n) => Math.max(0, Math.min(255, Math.round(n)))).join(', ')
}

function channelLuminance(value) {
	const x = value / 255
	return x <= 0.03928 ? x / 12.92 : Math.pow((x + 0.055) / 1.055, 2.4)
}

function luminance(color) {
	return 0.2126 * channelLuminance(color.r)
		+ 0.7152 * channelLuminance(color.g)
		+ 0.0722 * channelLuminance(color.b)
}

function contrast(a, b) {
	const la = luminance(a)
	const lb = luminance(b)
	return (Math.max(la, lb) + 0.05) / (Math.min(la, lb) + 0.05)
}

function mix(a, b, t) {
	return { r: a.r + (b.r - a.r) * t, g: a.g + (b.g - a.g) * t, b: a.b + (b.b - a.b) * t }
}

function readableOn(background) {
	return contrast(WHITE, background) >= contrast(BLACK, background) ? WHITE : BLACK
}

/**
 * Pulls a colour toward black or white until it clears `target` against its
 * background — the player's safety net, in 10% steps, keeping as much of the
 * chosen colour as the contrast budget allows.
 */
function ensureContrast(color, background, target) {
	if (contrast(color, background) >= target) return color

	const goal = readableOn(background)
	for (let t = 0.1; t < 1; t += 0.1) {
		const candidate = mix(color, goal, t)
		if (contrast(candidate, background) >= target) return candidate
	}
	return goal
}

/* ------------------------------ state ------------------------------- */

const normalized = computed(() => {
	const theme = props.theme || {}
	const preset = PRESET_ORDER.includes(theme.preset) || theme.preset === 'custom' ? theme.preset : 'dark'

	return {
		preset,
		background: theme.background || null,
		surface: theme.surface || null,
		text: theme.text || null,
		accent: theme.accent || null
	}
})

const isCustom = computed(() => normalized.value.preset === 'custom')

/** The palette a custom theme starts from: whatever is on screen right now. */
const basePalette = computed(() => {
	const preset = PRESETS[normalized.value.preset] || PRESETS.dark
	return {
		background: preset.background,
		surface: preset.surface,
		text: preset.text,
		accent: preset.accent
	}
})

// What is in the four inputs. Kept apart from the saved theme so a half-typed
// hex is not thrown away on the keystroke that makes it briefly invalid, and so
// the fields survive a trip through a preset and back.
const drafts = ref({ background: '', surface: '', text: '', accent: '' })

const syncDrafts = () => {
	FIELDS.forEach(({ key }) => {
		drafts.value[key] = normalized.value[key] || basePalette.value[key]
	})
}

watch(() => props.theme, syncDrafts, { immediate: true, deep: true })

function valid(hex) {
	return HEX_PATTERN.test(hex || '')
}

const invalidFields = computed(() => FIELDS.filter(({ key }) => !valid(drafts.value[key])).map((f) => f.key))

/**
 * The palette the player will actually paint, arrived at the same way it does:
 * preset colours unless the theme is custom, the accent falling back to the
 * stream's player theme colour, and every result held to its contrast floor.
 *
 * Showing the corrected colours rather than the requested ones is the point —
 * this is what a viewer sees.
 */
const resolved = computed(() => {
	const base = PRESETS[normalized.value.preset] || PRESETS.dark

	const pick = (key) => (isCustom.value ? parseColor(drafts.value[key]) : null)
		|| parseColor(base[key])
		|| parseColor(PRESETS.dark[key])

	const background = pick('background')
	let surface = pick('surface')
	const requestedText = pick('text')
	let requestedAccent = pick('accent')

	if (!(isCustom.value && parseColor(drafts.value.accent))) {
		const embed = parseColor(props.embedThemeColor)
		if (embed) requestedAccent = embed
	}

	// A surface equal to the background erases every card edge.
	const surfaceFlattened = contrast(surface, background) < 1.06
	if (surfaceFlattened) surface = mix(background, readableOn(background), 0.07)

	// The accent is a fill (3:1 as a UI component) and also link text (4.5:1),
	// which the player keeps as two separate values rather than one compromise.
	const accent = ensureContrast(requestedAccent, background, 3)
	const accentText = ensureContrast(requestedAccent, background, 4.5)
	const text = ensureContrast(requestedText, background, 4.5)
	const muted = ensureContrast(mix(requestedText, background, 0.38), background, 4.5)
	const accentOn = contrast(WHITE, accent) >= 3 ? WHITE : BLACK

	return {
		background: toHex(background),
		surface: toHex(surface),
		text: toHex(text),
		muted: toHex(muted),
		accent: toHex(accent),
		accentText: toHex(accentText),
		accentOn: toHex(accentOn),
		line: `rgba(${toTriplet(requestedText)}, 0.14)`,
		nudged: {
			text: toHex(text) !== toHex(requestedText),
			accent: toHex(accent) !== toHex(requestedAccent),
			accentText: toHex(accentText) !== toHex(requestedAccent),
			surface: surfaceFlattened
		}
	}
})

/**
 * What a host needs to know before this reaches viewers: not that the colours
 * are wrong, but that the player will move them, and by how much it matters.
 * Presets are never nudged, so this only ever speaks about a custom palette.
 */
const contrastNotice = computed(() => {
	if (!isCustom.value) return ''

	const nudged = resolved.value.nudged
	if (nudged.text) {
		return 'Your text colour is too low contrast to read on that background, so the player will lighten or darken it until it is legible. The preview shows the adjusted colour, not the one you picked.'
	}
	if (nudged.surface) {
		return 'Your cards are the same colour as the background, which would leave no visible edges, so the player will separate them slightly. The preview shows the adjusted colour.'
	}
	if (nudged.accent || nudged.accentText) {
		return 'Your accent is hard to pick out against the background, so the player will adjust it where it is used for buttons and links. The preview shows the adjusted colour.'
	}
	return ''
})

/* ------------------------------ actions ------------------------------ */

const choosePreset = (preset) => {
	if (props.disabled || normalized.value.preset === preset) return

	// Colours ride along untouched, so this is reversible.
	emit('update', { ...normalized.value, preset })
}

const chooseCustom = () => {
	if (props.disabled || isCustom.value) return

	// Seeded from the preset being left behind rather than left blank, so the
	// first thing a host sees under Custom is what they were already looking at.
	const seeded = {}
	FIELDS.forEach(({ key }) => {
		seeded[key] = normalized.value[key] || basePalette.value[key]
	})

	emit('update', { preset: 'custom', ...seeded })
}

const commit = (key, value) => {
	drafts.value[key] = value
	if (!valid(value)) return

	emit('update', { ...normalized.value, preset: 'custom', [key]: value })
}

const resetColours = () => {
	if (props.disabled) return

	const preset = PRESETS.dark
	const colours = {
		background: preset.background,
		surface: preset.surface,
		text: preset.text,
		accent: preset.accent
	}

	drafts.value = { ...colours }
	emit('update', { preset: 'custom', ...colours })
}

const previewMessages = [
	{ name: 'Priya', time: '19:04', text: 'Is the deck going to be shared afterwards?' },
	{ name: 'Marcus', time: '19:05', text: 'Audio is perfect here.' }
]
</script>

<template>
	<div class="grid gap-4 lg:grid-cols-[minmax(0,1fr),240px]">
		<div class="min-w-0">
			<div class="flex flex-wrap gap-2" role="radiogroup" aria-label="Panel theme">
				<button v-for="key in PRESET_ORDER" :key="key" type="button" role="radio"
					:aria-checked="normalized.preset === key" :disabled="disabled"
					class="flex items-center gap-x-2 text-sm px-3 py-2 rounded-lg border disabled:opacity-60"
					:class="normalized.preset === key
						? 'border-main bg-surface-2'
						: 'border-surface-4 hover:bg-surface-2'"
					@click="choosePreset(key)">
					<span class="flex shrink-0 rounded overflow-hidden border border-surface-4">
						<span class="w-3 h-4" :style="{ backgroundColor: PRESETS[key].background }"></span>
						<span class="w-3 h-4" :style="{ backgroundColor: PRESETS[key].surface }"></span>
						<span class="w-3 h-4" :style="{ backgroundColor: PRESETS[key].accent }"></span>
					</span>
					{{ PRESETS[key].label }}
				</button>

				<button type="button" role="radio" :aria-checked="isCustom" :disabled="disabled"
					class="text-sm px-3 py-2 rounded-lg border disabled:opacity-60"
					:class="isCustom ? 'border-main bg-surface-2' : 'border-surface-4 hover:bg-surface-2'"
					@click="chooseCustom">
					Custom
				</button>
			</div>

			<p v-if="!isCustom" class="text-tiny text-surface-7 mt-2 mb-0">
				<template v-if="embedThemeColor">
					Buttons and links use your player theme colour rather than the preset's accent.
				</template>
				<template v-else>
					Applies to the audience panel only. The player around it is unaffected.
				</template>
			</p>

			<div v-else class="mt-4">
				<div v-for="field in FIELDS" :key="field.key"
					class="flex items-center justify-between gap-x-3 py-2 border-t border-surface-3">
					<label class="min-w-0 mb-0" :for="`audience-theme-${field.key}`">
						<span class="block text-sm text-surface-text-1">{{ field.label }}</span>
						<span class="block text-tiny text-surface-7">{{ field.hint }}</span>
					</label>

					<div class="flex items-center gap-x-2 shrink-0">
						<input :id="`audience-theme-${field.key}`" type="color" :disabled="disabled"
							class="w-9 h-8 rounded border border-surface-4 bg-transparent p-0 cursor-pointer disabled:cursor-not-allowed"
							:value="valid(drafts[field.key]) ? drafts[field.key] : resolved[field.key]"
							@input="commit(field.key, $event.target.value)">
						<input type="text" spellcheck="false" :disabled="disabled" maxlength="7"
							:aria-label="`${field.label} hex value`"
							class="w-[92px] font-mono text-sm bg-surface-2 rounded-md px-2 py-1.5 border"
							:class="invalidFields.includes(field.key)
								? 'border-error text-error'
								: 'border-surface-4 text-surface-text-1'"
							:value="drafts[field.key]" @input="commit(field.key, $event.target.value)">
					</div>
				</div>

				<div class="flex items-center justify-between gap-x-3 pt-3">
					<p class="text-tiny text-surface-7 mb-0">
						<template v-if="!normalized.accent && embedThemeColor">
							Accent is coming from your player theme colour. Set one here to override it.
						</template>
						<template v-else>Six-digit hex, like #1a56db.</template>
					</p>
					<button type="button" class="text-tiny text-surface-8 hover:text-white underline"
						:disabled="disabled" @click="resetColours">
						Reset colours
					</button>
				</div>

				<Alert v-if="invalidFields.length" type="warning" class="mt-3"
					text="Colours must be six-digit hex values. The ones highlighted above are not being saved." />
				<Alert v-else-if="contrastNotice" type="warning" class="mt-3" :text="contrastNotice" />
			</div>
		</div>

		<!-- Four labelled swatches say nothing about what a host is choosing, so
		the preview is part of the control rather than a flourish. It is painted
		with the colours the player resolves, corrections included, so it shows
		what a viewer will get rather than what was typed. -->
		<div>
			<p class="text-tiny text-surface-7 mb-1.5">Preview</p>
			<div class="rounded-lg overflow-hidden border border-surface-3 select-none" aria-hidden="true"
				:style="{ backgroundColor: resolved.background, color: resolved.text }">
				<div class="flex text-tiny" :style="{ borderBottom: `1px solid ${resolved.line}` }">
					<span class="px-2.5 py-1.5 font-medium"
						:style="{ color: resolved.accentText, boxShadow: `inset 0 -2px 0 ${resolved.accent}` }">
						Chat
					</span>
					<span class="px-2.5 py-1.5" :style="{ color: resolved.muted }">Q&amp;A</span>
					<span class="px-2.5 py-1.5" :style="{ color: resolved.muted }">Polls</span>
				</div>

				<div class="p-2">
					<div class="rounded px-2 py-1.5 mb-2" :style="{ backgroundColor: resolved.surface }">
						<p class="text-[9px] uppercase tracking-wide mb-0.5"
							:style="{ color: resolved.accentText }">Pinned</p>
						<p class="text-tiny leading-snug mb-0">Doors close at half past.</p>
					</div>

					<div v-for="message in previewMessages" :key="message.name"
						class="rounded px-2 py-1.5 mb-1" :style="{ backgroundColor: resolved.surface }">
						<p class="text-tiny leading-snug mb-0">
							<span class="font-medium" :style="{ color: resolved.accentText }">
								{{ message.name }}
							</span>
							<span class="ml-1" :style="{ color: resolved.muted }">{{ message.time }}</span>
						</p>
						<p class="text-tiny leading-snug mb-0">{{ message.text }}</p>
					</div>

					<div class="flex items-center gap-x-1.5 mt-2">
						<span class="flex-1 text-tiny rounded px-2 py-1"
							:style="{ backgroundColor: resolved.surface, color: resolved.muted }">
							Say something
						</span>
						<span class="text-tiny rounded px-2 py-1 font-medium"
							:style="{ backgroundColor: resolved.accent, color: resolved.accentOn }">Send</span>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>
